import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import { listProducts, listSuggestions, getCatalogFacetCounts, extractProducts, productImages, productTitle, productHeading, productSubheading, productPrice, productPath, type CdnProduct, type CatalogFacetCounts } from "@/lib/cdnProducts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Search, Loader2, X } from "lucide-react";
import ProtectedImage from "@/components/parts/ProtectedImage";
import PartsCatalogFooter from "@/components/parts/PartsCatalogFooter";
import PartsBreadcrumb from "@/components/parts/PartsBreadcrumb";
import PartsCatalogSidebar from "@/components/parts/PartsCatalogSidebar";
import CatalogFilterRail, { countActiveFilters, type CatalogFilterState } from "@/components/parts/CatalogFilterRail";
import CatalogFilterSheet from "@/components/parts/CatalogFilterSheet";
import CatalogToolbar from "@/components/parts/CatalogToolbar";
import { STATIC_ASSETS } from "@/lib/imageUtils";
import { useHeroContent } from "@/hooks/useHeroContent";
import HeroButtons from "@/components/ui/HeroButtons";
import { useOptimizedImg } from "@/hooks/useOptimizedImg";

import CarmaxPageHero from "@/components/carmax/CarmaxPageHero";
import heroVideoDesktopAsset from "@/assets/parts-hero/hero-parts-1280.mp4.asset.json";
import { useThemeLayout } from "@/hooks/useThemeLayout";
import LazySection from "@/components/ui/LazySection";

const LIMIT = 20;

function ResultThumb({ src, alt }: { src: string; alt: string }) {
  const optimized = useOptimizedImg(src, "catalog");
  return <ProtectedImage src={optimized} alt={alt} className="w-full h-full" objectFit="contain" loading="lazy" />;
}

function highlightMatch(text: string, query: string) {
  const q = query.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-primary font-semibold">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function PartsCatalogResults() {
  const layout = useThemeLayout();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromHere = `${location.pathname}${location.search}`;
  const rememberFrom = () => {
    try { sessionStorage.setItem("parts-catalog:lastResults", fromHere); } catch {}
  };
  const page = Math.max(1, Number(params.get("page") ?? "1"));
  const search = params.get("search") ?? "";
  const category = params.get("category") ?? "";
  const vendor = params.get("vendor") ?? "";
  const vendorId = params.get("vendor_id") ?? "";
  const sort = params.get("sort") ?? "name_asc";
  const newOnly = params.get("new") === "1";
  const seriesParam = params.get("series") ?? "";
  const series = seriesParam ? seriesParam.split("|").filter(Boolean) : [];
  const inStock = params.get("instock") === "1";
  const priceMin = params.get("price_min") ?? "";
  const priceMax = params.get("price_max") ?? "";
  const filters: CatalogFilterState = { category, vendor, series, inStock, priceMin, priceMax };
  const activeFilterCount = countActiveFilters(filters);
  const [query, setQuery] = useState(search);
  const [products, setProducts] = useState<CdnProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<CatalogFacetCounts | undefined>(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [vendorIdLabel, setVendorIdLabel] = useState<string>("");
  const [suggestions, setSuggestions] = useState<CdnProduct[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const suggestBoxRef = useRef<HTMLFormElement>(null);

  const { data: cmsHero } = useHeroContent("parts-catalog", "hero_slides");
  const cmsSlide = cmsHero?.slides?.[0];
  const heroTitle = cmsSlide?.title || "RV Parts Catalog";
  const heroSubtitle = cmsSlide?.subtitle;
  const heroDesktop = cmsSlide?.image_desktop;
  const heroMobile = cmsSlide?.image_mobile;
  const heroButtons = cmsSlide?.buttons;

  useEffect(() => { setQuery(search); }, [search]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }
    setSuggestLoading(true);
    const t = setTimeout(() => {
      listSuggestions(q, 3)
        .then((items) => {
          setSuggestions(items);
          setActiveIdx(-1);
        })
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target as Node)) {
        setShowSuggest(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listProducts({
      page,
      limit: LIMIT,
      search: search || undefined,
      category: category || undefined,
      vendor: vendor || undefined,
      vendorId: vendorId || undefined,
      sort,
      newOnly: newOnly || undefined,
      series: series.length ? series : undefined,
      inStock: inStock || undefined,
      priceMin: priceMin ? Number(priceMin) : undefined,
      priceMax: priceMax ? Number(priceMax) : undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const items = extractProducts(res);
        setProducts(items);
        if (typeof (res as any).total === "number") setTotal((res as any).total);
        setHasMore(typeof res.totalPages === "number" ? page < res.totalPages : items.length === LIMIT);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [page, search, category, vendor, vendorId, sort, newOnly, seriesParam, inStock, priceMin, priceMax]);

  useEffect(() => {
    if (!vendorId) { setVendorIdLabel(""); return; }
    let cancelled = false;
    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase
        .from("parts_manufacturers")
        .select("name")
        .eq("vendor_id", vendorId)
        .maybeSingle()
        .then(({ data }) => { if (!cancelled) setVendorIdLabel(data?.name ?? vendorId); });
    });
    return () => { cancelled = true; };
  }, [vendorId]);

  useEffect(() => {
    let cancelled = false;
    getCatalogFacetCounts({
      search: search || undefined,
      category: category || undefined,
      vendor: vendor || undefined,
      vendorId: vendorId || undefined,
      series: series.length ? series : undefined,
      inStock: inStock || undefined,
      priceMin: priceMin ? Number(priceMin) : undefined,
      priceMax: priceMax ? Number(priceMax) : undefined,
    })
      .then((f) => { if (!cancelled) setFacets(f); })
      .catch((e) => console.error("[parts facets] failed", e));
    return () => { cancelled = true; };
  }, [search, category, vendor, vendorId, seriesParam, inStock, priceMin, priceMax]);

  function applyFilters(f: CatalogFilterState) {
    const next = new URLSearchParams(params);
    const setOrDel = (key: string, val: string) => {
      if (val) next.set(key, val); else next.delete(key);
    };
    setOrDel("category", f.category);
    setOrDel("vendor", f.vendor);
    setOrDel("series", f.series.join("|"));
    setOrDel("price_min", f.priceMin);
    setOrDel("price_max", f.priceMax);
    if (f.inStock) next.set("instock", "1"); else next.delete("instock");
    next.delete("page");
    setParams(next);
  }

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (query) next.set("search", query); else next.delete("search");
    next.delete("page");
    setParams(next);
    setShowSuggest(false);
  }

  function goPage(n: number) {
    const next = new URLSearchParams(params);
    next.set("page", String(n));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function selectCategory(name: string | null) {
    const next = new URLSearchParams(params);
    if (name) next.set("category", name); else next.delete("category");
    next.delete("page");
    setParams(next);
  }

  function selectVendor(name: string | null) {
    const next = new URLSearchParams(params);
    if (name) next.set("vendor", name); else next.delete("vendor");
    next.delete("page");
    setParams(next);
  }

  function clearVendorId() {
    const next = new URLSearchParams(params);
    next.delete("vendor_id");
    next.delete("page");
    setParams(next);
  }

  function clearNewOnly() {
    const next = new URLSearchParams(params);
    next.delete("new");
    next.delete("page");
    setParams(next);
  }

  function selectSort(value: string) {
    const next = new URLSearchParams(params);
    if (value && value !== "name_asc") next.set("sort", value); else next.delete("sort");
    next.delete("page");
    setParams(next);
  }

  const searchForm = (
          <form
            onSubmit={submitSearch}
            className="mx-auto mt-6 flex max-w-2xl gap-2 relative z-[1000]"
            ref={suggestBoxRef}
          >
            <div className="relative flex-1">
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onKeyDown={(e) => {
                  if (!showSuggest || suggestions.length === 0) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveIdx((i) => Math.max(i - 1, -1));
                  } else if (e.key === "Enter" && activeIdx >= 0) {
                    e.preventDefault();
                    const p = suggestions[activeIdx];
                    if (p) {
                      setShowSuggest(false);
                      rememberFrom();
                      navigate(productPath(p), { state: { from: fromHere } });
                    }
                  } else if (e.key === "Escape") {
                    setShowSuggest(false);
                  }
                }}
                placeholder="Search parts..."
                autoComplete="off"
                className="h-12 bg-background/95 text-base shadow-lg"
              />
              {showSuggest && query.trim().length >= 3 && (
                <div className="absolute z-[1000] left-0 right-0 top-full mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden text-left">
                  {suggestLoading && suggestions.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                    </div>
                  ) : suggestions.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground">No matches</div>
                  ) : (
                    <ul className="max-h-96 overflow-auto">
                      {suggestions.map((p, i) => {
                        const id = String(p.ProductID);
                        const title = productTitle(p);
                        const img = productImages(p)[0];
                        const price = productPrice(p);
                        return (
                          <li key={id}>
                            <Link
                              to={productPath(p)}
                              state={{ from: fromHere } as never}
                              onClick={() => { setShowSuggest(false); rememberFrom(); }}
                              onMouseEnter={() => setActiveIdx(i)}
                              className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${i === activeIdx ? "bg-muted" : "hover:bg-muted"}`}
                            >
                              <div className="w-10 h-10 flex-shrink-0 bg-muted rounded-sm flex items-center justify-center overflow-hidden">
                                {img ? (
                                  <ProtectedImage src={img} alt={title} className="w-full h-full" objectFit="contain" loading="lazy" />
                                ) : (
                                  <span className="text-[10px] text-muted-foreground">No img</span>
                                )}
                              </div>
                              <span className="flex-1 line-clamp-1">{highlightMatch(title, query)}</span>
                              {price != null && (
                                <span className="text-primary font-semibold whitespace-nowrap">${price.toFixed(2)}</span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                      <li className="border-t border-border">
                        <button
                          type="submit"
                          className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-muted font-medium"
                        >
                          See all results for "{query.trim()}"
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              )}
            </div>
            <Button type="submit" size="lg" className="h-12">
              <Search className="w-4 h-4 mr-2" />Search
            </Button>
          </form>
  );

  return (
    <>
      <Helmet>
        <title>Parts Search Results | Rex & Sons RVs</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {layout === "carmax" ? (
        <>
          <CarmaxPageHero optimize={false} video={heroVideoDesktopAsset.url} cmsSlug="parts-catalog" slides={[{ image: heroDesktop || STATIC_ASSETS.heroService1920, imageMobile: heroMobile || STATIC_ASSETS.heroService640, title: heroTitle, ...(heroSubtitle ? { subtitle: heroSubtitle } : {}) }]}>
            {heroButtons && heroButtons.length > 0 ? <HeroButtons buttons={heroButtons} /> : undefined}
          </CarmaxPageHero>
          <div className="container relative z-[100] pb-4">{searchForm}</div>
        </>
      ) : (
      <section className="relative z-[100] flex min-h-[420px] md:min-h-[520px] items-center justify-center overflow-visible bg-secondary -mt-[140px] pt-[140px]">
        <picture className="absolute inset-0 h-full w-full">
          <source srcSet={heroMobile || STATIC_ASSETS.heroService640} media="(max-width: 768px)" type="image/webp" />
          <source srcSet={heroDesktop || STATIC_ASSETS.heroService1920} media="(min-width: 769px)" type="image/webp" />
          <img
            src={heroDesktop || STATIC_ASSETS.heroService1920}
            alt=""
            className="h-full w-full object-cover"
            loading="eager"
            decoding="sync"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/90 via-secondary/70 to-secondary/90" />
        <div className="relative z-10 container py-10 text-center">
          <h1 className="font-heading text-4xl font-bold uppercase tracking-wide text-primary-foreground md:text-5xl lg:text-6xl">
            {heroTitle}
          </h1>
          {heroSubtitle && (
            <p className="mx-auto mt-4 max-w-2xl text-lg text-secondary-foreground/80">
              {heroSubtitle}
            </p>
          )}
          {searchForm}
          {heroButtons && heroButtons.length > 0 && (
            <div className="mt-6">
              <HeroButtons buttons={heroButtons} />
            </div>
          )}
        </div>
      </section>
      )}

      <div className="container mx-auto px-4 py-8">
        <PartsBreadcrumb crumbs={[
          { label: "Parts Catalog", to: "/parts-catalog" },
          { label: search ? `Search: "${search}"` : category ? `Category: ${category}` : vendor ? `Manufacturer: ${vendor}` : "Results" },
        ]} />
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          <aside className="hidden lg:block">
            <PartsCatalogSidebar />
            <div className="mt-6">
              <CatalogFilterRail
                value={filters}
                onChange={applyFilters}
                {...(facets !== undefined ? { facets } : {})}
                loading={loading}
              />
            </div>
          </aside>

          <div>
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex flex-wrap items-center gap-2">
                {vendorIdLabel && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
                    Brand: {vendorIdLabel}
                    <button type="button" onClick={clearVendorId} className="hover:opacity-70" aria-label="Clear brand filter">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {newOnly && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
                    New products
                    <button type="button" onClick={clearNewOnly} className="hover:opacity-70" aria-label="Clear new filter">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
              <div className="relative z-[150]">
                <CatalogToolbar
                  sort={sort}
                  onSortChange={selectSort}
                  hasSearch={!!search}
                  resultCount={facets?.total ?? total}
                  activeFilterCount={activeFilterCount}
                  onOpenFilters={() => setFiltersOpen(true)}
                />
              </div>
            </div>

            <CatalogFilterSheet
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              value={filters}
              onApply={applyFilters}
              facets={facets}
              resultCount={facets?.total ?? total}
            />

            {loading && (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading products...
              </div>
            )}

            {!loading && (error || products.length === 0) && (
              <p className="text-muted-foreground py-8 text-center">No items found.</p>
            )}

            {newOnly && !loading && products.length > 0 && (
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-wide mb-4">
                Just Arrived
              </h2>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => {
                const id = String(p.ProductID);
                const title = productTitle(p);
                const heading = productHeading(p);
                const subheading = productSubheading(p);
                const img = productImages(p)[0];
                const price = productPrice(p);
                return (
                  <LazySection key={id} fallback={<div className="aspect-square" />}><Link to={productPath(p)} state={{ from: fromHere } as never} onClick={rememberFrom}>
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                      <div className="aspect-square bg-muted flex items-center justify-center parts-no-save">
                        {img ? (
                          <ResultThumb src={img} alt={title} />
                        ) : (
                          <span className="text-xs text-muted-foreground">No image</span>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="text-sm font-semibold line-clamp-2">{heading}</div>
                        {subheading && (
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{subheading}</div>
                        )}
                      {(p.InventoryTotal ?? 0) <= 0 ? (
                        <div className="text-destructive font-bold mt-1">Out of Stock</div>
                      ) : price != null ? (
                        <div className="text-primary font-bold mt-1">${price.toFixed(2)}</div>
                      ) : null}
                      </div>
                    </Card>
                  </Link></LazySection>
                );
              })}
            </div>

            {(page > 1 || hasMore) && (
              <div className="flex justify-center gap-3 mt-8">
                <Button variant="outline" disabled={page <= 1} onClick={() => goPage(page - 1)}>
                  Previous
                </Button>
                <span className="self-center text-sm text-muted-foreground">Page {page}</span>
                <Button variant="outline" disabled={!hasMore} onClick={() => goPage(page + 1)}>
                  Next
                </Button>
              </div>
            )}

          </div>
        </div>
      </div>
      <LazySection fallback={<div className="min-h-[400px]" />}><PartsCatalogFooter /></LazySection>
    </>
  );
}